import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Popover,
    Button,
    IconButton,
    Grid,
    Paper
} from '@mui/material';
import { TextField } from '@mui/material';
import {
    RefreshIcon,
    CalendarTodayOutlinedIcon,
    AddIcon,
    OpenInNewIcon,
    CloseIcon,
    FilterAltOutlinedIcon,
    PersonAddAlt1OutlinedIcon,
    KeyboardArrowDownOutlinedIcon
} from 'icons/materialIcons';
import { KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from 'icons/materialIcons';
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    isWithinInterval
} from 'date-fns';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import DarkButtonWithIcon from 'components/common/button/DarkButtonWithIcon';
import SearchBar from '../../../../components/contacts/contactMain/SearchBar';
import PropTypes from 'prop-types';
import {
    formatDate,
    generateCalendarDays
} from 'utils/dateUtils';
import TeamV2AddLeadsModal from './addLeads/TeamV2AddLeadsModal';
import TeamV2CreateLeadModal from './addLeads/TeamV2CreateLeadModal';

const TeamV2LeadsMenu = ({
    totalLeads,
    onRefresh,
    onAddLeads,
    onExport,
    leads = [],
    selectedCount = 0,
    onDateRangeChange,
    searchQuery = '',
    onSearchChange,
    onImportCSV
}) => {
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isAddLeadsModalOpen, setIsAddLeadsModalOpen] = useState(false);
    const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
    const exportButtonRef = useRef(null);
    const dateButtonRef = useRef(null);
    const addLeadsButtonRef = useRef(null);

    // Default date range - initially set to all time
    const [dateRange, setDateRange] = useState([
        new Date('2020-01-01'),
        new Date()
    ]);

    // Current calendar view - month being displayed
    const [calendarViewDate, setCalendarViewDate] = useState(new Date());

    // State for date picker popover
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);

    // State for filter popover
    const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
    const [filterSearch, setFilterSearch] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({
        subteams: false,
        members: false,
        removedMembers: false,
        leadType: false
    });
    const [tempDateRange, setTempDateRange] = useState(dateRange);
    const [currentStep, setCurrentStep] = useState(0); // 0 = start date, 1 = end date

    // Predefined date ranges
    const getPredefinedRanges = () => {
        const today = new Date();

        // Helper function to get date N days ago
        const daysAgo = (days) => {
            const date = new Date(today);
            date.setDate(date.getDate() - days);
            return date;
        };

        // Helper function to get first day of month
        const firstDayOfMonth = (monthsAgo = 0) => {
            const date = new Date(today);
            date.setMonth(date.getMonth() - monthsAgo);
            date.setDate(1);
            return date;
        };

        return [
            { label: 'Today', range: [today, today] },
            { label: 'Yesterday', range: [daysAgo(1), daysAgo(1)] },
            { label: 'Last 7 days', range: [daysAgo(6), today] },
            { label: 'Last 30 days', range: [daysAgo(29), today] },
            { label: 'This month', range: [firstDayOfMonth(0), today] },
            { label: 'Last month', range: [firstDayOfMonth(1), firstDayOfMonth(0)] },
            { label: 'This year', range: [new Date(today.getFullYear(), 0, 1), today] },
            { label: 'All time', range: [new Date('2020-01-01'), today] }
        ];
    };

    const predefinedRanges = getPredefinedRanges();

    // Format month and year for display
    const formatMonthYear = (date) => {
        return format(date, 'MMMM yyyy');
    };

    // Format full date for display
    const formatFullDate = (date) => {
        return format(date, 'MMM dd, yyyy');
    };

    // Generate calendar days for the current month view
    const generateCalendarDays = (date) => {
        const monthStart = startOfMonth(date);
        const monthEnd = endOfMonth(date);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const days = [];
        let day = startDate;

        while (day <= endDate) {
            days.push({
                day: isSameMonth(day, monthStart) ? day.getDate() : null,
                date: isSameMonth(day, monthStart) ? new Date(day) : null,
                isCurrentMonth: isSameMonth(day, monthStart)
            });
            day = addDays(day, 1);
        }

        return days;
    };

    const calendarDays = generateCalendarDays(calendarViewDate);

    // Custom isInRange function since date-fns doesn't have it
    const isInRange = (date, startDate, endDate) => {
        if (!date || !startDate || !endDate) return false;
        return date >= startDate && date <= endDate;
    };

    // Handle date range selection
    const handleDateRangeSelect = (range) => {
        setDateRange(range);
        setTempDateRange(range);
        setDatePopoverOpen(false);
        onDateRangeChange && onDateRangeChange(range);
    };

    // Handle applying the selected date range
    const handleApplyDateRange = () => {
        let sortedRange = [...tempDateRange];
        if (tempDateRange[0] > tempDateRange[1]) {
            sortedRange = [tempDateRange[1], tempDateRange[0]];
        }
        setDateRange(sortedRange);
        setDatePopoverOpen(false);
        onDateRangeChange && onDateRangeChange(sortedRange);
    };

    // Handle date selection in calendar
    const handleDateSelect = (date) => {
        if (currentStep === 0) {
            // Selecting start date
            setTempDateRange([date, tempDateRange[1]]);
            setCurrentStep(1);
        } else {
            // Selecting end date
            const newRange = [tempDateRange[0], date];
            setTempDateRange(newRange);
            setDateRange(newRange);
            setDatePopoverOpen(false);
            setCurrentStep(0);
            onDateRangeChange && onDateRangeChange(newRange);
        }
    };

    // Handle predefined range selection
    const handleSelectPredefinedRange = (range) => {
        setDateRange(range);
        setTempDateRange(range);
        setDatePopoverOpen(false);
        onDateRangeChange && onDateRangeChange(range);
    };

    // Filter handlers
    const handleFilterClick = (event) => {
        setFilterPopoverOpen(true);
    };

    const handleFilterClose = () => {
        setFilterPopoverOpen(false);
    };

    const handleFilterSearchChange = (event) => {
        setFilterSearch(event.target.value);
    };

    const handleFilterOptionChange = (option) => {
        setSelectedFilters(prev => ({
            ...prev,
            [option]: !prev[option]
        }));
    };

    const handleClearFilters = () => {
        setSelectedFilters({
            subteams: false,
            members: false,
            removedMembers: false,
            leadType: false
        });
        setFilterSearch('');
    };

    const handleApplyFilters = () => {
        // Apply filters logic here
        console.log('Applied filters:', selectedFilters, 'Search:', filterSearch);
        setFilterPopoverOpen(false);
    };

    // Calendar navigation
    const handlePrevMonth = () => {
        setCalendarViewDate(prev => addMonths(prev, -1));
    };

    const handleNextMonth = () => {
        setCalendarViewDate(prev => addMonths(prev, 1));
    };

    // Get current range display text
    const getRangeDisplayText = () => {
        const dateRangeText = `${formatFullDate(dateRange[0])} - ${formatFullDate(dateRange[1])}`;

        // Check if it's "All Time" (from 2020-01-01 to today)
        if (isSameDay(dateRange[0], new Date('2020-01-01')) && isSameDay(dateRange[1], new Date())) {
            return { label: 'All time', dateRange: dateRangeText };
        }

        // Check if it's "This Year" (from start of current year to today)
        const startOfYear = new Date(new Date().getFullYear(), 0, 1);
        if (isSameDay(dateRange[0], startOfYear) && isSameDay(dateRange[1], new Date())) {
            return { label: 'This Year', dateRange: dateRangeText };
        }

        // Check if it's "Last Month"
        const startOfLastMonth = new Date();
        startOfLastMonth.setMonth(startOfLastMonth.getMonth() - 1);
        startOfLastMonth.setDate(1);
        const endOfLastMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0);
        if (isSameDay(dateRange[0], startOfLastMonth) && isSameDay(dateRange[1], endOfLastMonth)) {
            return { label: 'Last Month', dateRange: dateRangeText };
        }

        if (isSameDay(dateRange[0], dateRange[1])) {
            return { label: formatFullDate(dateRange[0]), dateRange: '' };
        }
        return { label: '', dateRange: dateRangeText };
    };

    // Export handlers
    const handleExportClick = () => {
        onExport && onExport('csv');
    };

    // Handle add leads button click
    const handleAddLeadsClick = (e) => {
        addLeadsButtonRef.current = e.currentTarget;
        setIsAddLeadsModalOpen(true);
    };

    // Handle add leads modal close
    const handleAddLeadsModalClose = () => {
        setIsAddLeadsModalOpen(false);
    };

    // Handle add leads modal select
    const handleAddLeadsModalSelect = (option) => {
        setIsAddLeadsModalOpen(false);
        if (option === 'create') {
            setIsCreateLeadModalOpen(true);
        } else if (option === 'import') {
            onImportCSV && onImportCSV();
        }
    };

    // Handle create lead modal close
    const handleCreateLeadModalClose = () => {
        setIsCreateLeadModalOpen(false);
    };

    // Handle create lead success
    const handleCreateLeadSuccess = () => {
        setIsCreateLeadModalOpen(false);
        onRefresh && onRefresh();
    };

    // Handle notifications
    const handleNotification = (message, severity) => {
        console.log('Notification:', message, severity);
    };

    return (
        <Box sx={{ mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
                {/* Left side - Title, Count, and Buttons */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            color: '#4f4f4f'
                        }}>
                            Leads <span style={{ color: '#828282', fontSize: '0.875rem', fontWeight: 'normal' }}>({totalLeads})</span>
                        </Typography>

                        {/* Refresh Button */}
                        <Box sx={{ ml: 4 }}>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                onClick={onRefresh}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#4f4f4f',
                                    borderRadius: '1.25rem',
                                    border: '0.0625rem solid #e3e3e3',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    minWidth: '7.5rem',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                        boxShadow: 2,
                                    },
                                    '& .MuiButton-startIcon': {
                                        mr: 1,
                                    }
                                }}
                            >
                                Refresh
                            </Button>
                        </Box>

                        {/* Filter Button */}
                        <Box sx={{ ml: 1 }}>
                            <Button
                                variant="contained"
                                startIcon={<FilterAltOutlinedIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                onClick={handleFilterClick}
                                data-filter-button
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#4f4f4f',
                                    borderRadius: '1.25rem',
                                    border: '0.0625rem solid #e3e3e3',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    minWidth: '8rem',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                        boxShadow: 2,
                                    },
                                    '& .MuiButton-startIcon': {
                                        mr: 1,
                                    }
                                }}
                            >
                                Filter
                            </Button>
                        </Box>

                        {/* Search Bar - Made wider */}
                        <Box sx={{ minWidth: '15.625rem', maxWidth: '18.75rem', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{
                                width: '100%',
                                '& .MuiBox-root': {
                                    marginBottom: 0,
                                    border: 'none !important'
                                },
                                '& .MuiInputBase-root': {
                                    fontSize: '0.7rem !important',
                                    '& .MuiInputBase-input::placeholder': {
                                        color: '#A0A0A0 !important',
                                        fontWeight: '400 !important',
                                        fontSize: '0.7rem !important',
                                        opacity: 1
                                    }
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '0.7rem !important',
                                    '&::placeholder': {
                                        color: '#A0A0A0 !important',
                                        fontWeight: '400 !important',
                                        fontSize: '0.7rem !important',
                                        opacity: 1
                                    }
                                }
                            }}>
                                <SearchBar
                                    value={searchQuery}
                                    onChange={onSearchChange}
                                    placeholder="Search name, email or company"
                                />
                            </Box>
                        </Box>

                        {/* Calendar Filter Button */}
                        <Box>
                            <Button
                                ref={dateButtonRef}
                                variant="contained"
                                startIcon={<CalendarTodayOutlinedIcon sx={{ color: '#828282', fontSize: '0.875rem', '& .MuiSvgIcon-root': { fontSize: '0.875rem' } }} />}
                                onClick={(e) => {
                                    setDatePopoverOpen(true);
                                    dateButtonRef.current = e.currentTarget;
                                }}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#4f4f4f',
                                    borderRadius: '1.25rem',
                                    border: 'none',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    minWidth: '11.25rem',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                        boxShadow: 2,
                                    },
                                    '& .MuiButton-startIcon': {
                                        mr: 2,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '0.875rem !important'
                                        }
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    {getRangeDisplayText().label && (
                                        <Box component="span" sx={{ color: '#000000', fontWeight: '600' }}>
                                            {getRangeDisplayText().label}
                                        </Box>
                                    )}
                                    {getRangeDisplayText().dateRange && (
                                        <Box component="span" sx={{ color: '#828282', fontWeight: '600', fontSize: '0.65rem' }}>
                                            {getRangeDisplayText().dateRange}
                                        </Box>
                                    )}
                                </Box>
                            </Button>
                        </Box>

                        {/* Add Leads Button */}
                        <Box>
                            <Button
                                ref={addLeadsButtonRef}
                                variant="contained"
                                startIcon={<AddIcon sx={{ color: '#828282', fontSize: '0.875rem' }} />}
                                onClick={handleAddLeadsClick}
                                sx={{
                                    backgroundColor: 'white',
                                    color: '#4f4f4f',
                                    borderRadius: '1.25rem',
                                    border: 'none',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    minWidth: '7.25rem',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    '&:hover': {
                                        backgroundColor: 'white',
                                        boxShadow: 2,
                                    },
                                    '& .MuiButton-startIcon': {
                                        mr: 1,
                                    }
                                }}
                            >
                                Add Leads
                            </Button>
                        </Box>

                        {/* Export Button */}
                        <Box>
                            <Button
                                variant="contained"
                                startIcon={<PersonAddAlt1OutlinedIcon sx={{ color: '#fff', fontSize: '0.875rem' }} />}
                                onClick={handleExportClick}
                                sx={{
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    borderRadius: '1.25rem',
                                    border: 'none',
                                    boxShadow: 'none',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: '600',
                                    padding: '0.5rem 1rem',
                                    minWidth: '6.875rem',
                                    '&:hover': {
                                        backgroundColor: '#000',
                                        boxShadow: 2,
                                    },
                                    '& .MuiButton-startIcon': {
                                        mr: 1,
                                    }
                                }}
                            >
                                Export
                            </Button>
                        </Box>

                    </Box>
                </Grid>

            </Grid>

            {/* Enhanced Date Range Picker Popover */}
            <Popover
                open={datePopoverOpen}
                anchorEl={dateButtonRef.current}
                onClose={() => setDatePopoverOpen(false)}
                disablePortal={false}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                sx={{
                    zIndex: 1300,
                    mt: 0.5,
                    '& .MuiBackdrop-root': {
                        zIndex: 1299
                    }
                }}
                PaperProps={{
                    sx: {
                        width: 560,
                        borderRadius: '16px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                        padding: 0,
                        overflow: 'hidden',
                        position: 'relative',
                        zIndex: 1300
                    }
                }}
            >
                {/* Calendar Header */}
                <Box sx={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                        Select Date Range
                    </Typography>
                    <IconButton size="small" onClick={() => setDatePopoverOpen(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Calendar */}
                <Box sx={{ display: 'flex' }}>
                    {/* Preset Ranges */}
                    <Box sx={{
                        width: '160px',
                        borderRight: '1px solid #e0e0e0',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
                            Preset Ranges
                        </Typography>

                        {predefinedRanges.map((preset, index) => (
                            <Button
                                key={index}
                                onClick={() => handleSelectPredefinedRange(preset.range)}
                                variant="text"
                                size="small"
                                sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    color:
                                        (dateRange[0] && dateRange[1] &&
                                            isSameDay(dateRange[0], preset.range[0]) &&
                                            isSameDay(dateRange[1], preset.range[1])) ? '#000' : '#666',
                                    fontWeight:
                                        (dateRange[0] && dateRange[1] &&
                                            isSameDay(dateRange[0], preset.range[0]) &&
                                            isSameDay(dateRange[1], preset.range[1])) ? 600 : 400,
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.05)'
                                    },
                                    padding: '4px 8px',
                                    minHeight: '32px',
                                    fontSize: '13px'
                                }}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </Box>

                    {/* Calendar View */}
                    <Box sx={{ flex: 1, padding: '0' }}>
                        {/* Month/Year Navigation */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <IconButton size="small" onClick={handlePrevMonth}>
                                <KeyboardArrowLeftIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                {formatMonthYear(calendarViewDate)}
                            </Typography>
                            <IconButton size="small" onClick={handleNextMonth}>
                                <KeyboardArrowRightIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        {/* Custom Calendar Grid */}
                        <Box sx={{ padding: '16px' }}>
                            {/* Weekday headers */}
                            <Grid container>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                                    <Grid item xs={12 / 7} key={index} sx={{ textAlign: 'center' }}>
                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>
                                            {day}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Calendar days */}
                            <Grid container sx={{ mt: 1 }}>
                                {calendarDays.map((dayObj, index) => (
                                    <Grid item xs={12 / 7} key={index}>
                                        {dayObj.day !== null && (
                                            <Box
                                                onClick={() => handleDateSelect(dayObj.date)}
                                                sx={{
                                                    height: '40px',
                                                    width: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    borderRadius: '50%',
                                                    margin: '4px auto',

                                                    // Highlight start and end dates
                                                    ...(tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0]) && {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                    }),

                                                    ...(tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]) && {
                                                        backgroundColor: '#000',
                                                        color: '#fff',
                                                    }),

                                                    // Highlight range
                                                    ...(tempDateRange[0] && tempDateRange[1] &&
                                                        isInRange(dayObj.date, tempDateRange[0], tempDateRange[1]) && {
                                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                                    }),

                                                    // Highlight today
                                                    ...(isSameDay(dayObj.date, new Date()) && !isSameDay(dayObj.date, tempDateRange[0]) &&
                                                        !isSameDay(dayObj.date, tempDateRange[1]) && {
                                                        border: '1px solid #000',
                                                    }),

                                                    '&:hover': {
                                                        backgroundColor:
                                                            (tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) ||
                                                                (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]))
                                                                ? '#000' : 'rgba(0,0,0,0.2)',
                                                        color:
                                                            (tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) ||
                                                                (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]))
                                                                ? '#fff' : 'inherit',
                                                    },
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '14px' }}>
                                                    {dayObj.day}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                </Box>

                {/* Selected Range Display & Buttons */}
                <Box sx={{
                    padding: '16px',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                Start Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[0] ? formatFullDate(tempDateRange[0]) : 'Select date'}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                End Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[1] ? formatFullDate(tempDateRange[1]) : 'Select date'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Button
                            onClick={() => handleDateRangeSelect([new Date('2020-01-01'), new Date()])}
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: '#e0e0e0',
                                color: '#666',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': {
                                    borderColor: '#c0c0c0',
                                    backgroundColor: 'rgba(0,0,0,0.02)'
                                }
                            }}
                        >
                            Reset to All Time
                        </Button>

                        <Button
                            onClick={handleApplyDateRange}
                            variant="contained"
                            size="small"
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': {
                                    backgroundColor: '#333'
                                }
                            }}
                        >
                            Apply Filter
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {/* Filter Popover */}
            <Popover
                open={filterPopoverOpen}
                onClose={handleFilterClose}
                anchorEl={filterPopoverOpen ? document.querySelector('[data-filter-button]') : null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        borderRadius: '12px',
                        minWidth: 300,
                        maxWidth: 400,
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e0e0e0'
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    {/* Search Field - Using options background color with main search shape */}
                    <Box sx={{
                        mb: 2,
                        '& .MuiBox-root': {
                            marginBottom: 0,
                            border: 'none !important',
                            backgroundColor: '#f7f7f7 !important',
                            borderRadius: '20px !important'
                        },
                        '& .MuiInputBase-root': {
                            fontSize: '0.7rem !important',
                            backgroundColor: 'transparent !important',
                            '& .MuiInputBase-input::placeholder': {
                                color: '#A0A0A0 !important',
                                fontWeight: '400 !important',
                                fontSize: '0.7rem !important',
                                opacity: 1
                            }
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '0.7rem !important',
                            backgroundColor: 'transparent !important',
                            '&::placeholder': {
                                color: '#A0A0A0 !important',
                                fontWeight: '400 !important',
                                fontSize: '0.7rem !important',
                                opacity: 1
                            }
                        }
                    }}>
                        <SearchBar
                            value={filterSearch}
                            onChange={handleFilterSearchChange}
                            placeholder="Search team members or members..."
                        />
                    </Box>

                    {/* Filter Options */}
                    <Box sx={{ mb: 2 }}>
                        {[
                            { key: 'subteams', label: 'Subteams' },
                            { key: 'members', label: 'Members' },
                            { key: 'removedMembers', label: 'Removed members' },
                            { key: 'leadType', label: 'Lead type' }
                        ].map((option) => (
                            <Box
                                key={option.key}
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: '0.25rem',
                                    padding: '0.75rem 1rem',
                                    minHeight: '3rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer',
                                    marginBottom: '0.75rem',
                                    '&:hover': {
                                        backgroundColor: '#f0f0f0'
                                    },
                                    '&:focus-within': {
                                        outline: '0.125rem solid #1976d2',
                                        outlineOffset: '-0.125rem'
                                    }
                                }}
                                onClick={() => handleFilterOptionChange(option.key)}
                            >
                                <Typography sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    color: '#666666',
                                    lineHeight: 1.5
                                }}>
                                    {option.label}
                                </Typography>
                                <KeyboardArrowDownOutlinedIcon
                                    sx={{
                                        fontSize: '1.25rem',
                                        color: '#666',
                                        transform: selectedFilters[option.key] ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease-in-out'
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            sx={{
                                backgroundColor: 'white',
                                color: '#828282',
                                borderRadius: '20px',
                                border: 'none',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': {
                                    backgroundColor: 'white',
                                    boxShadow: 2,
                                }
                            }}
                        >
                            Clear filter
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApplyFilters}
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                borderRadius: '20px',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '6px 16px',
                                '&:hover': {
                                    backgroundColor: '#333'
                                }
                            }}
                        >
                            Apply filter
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {/* Add Leads Modal */}
            <TeamV2AddLeadsModal
                isOpen={isAddLeadsModalOpen}
                onClose={handleAddLeadsModalClose}
                onSelect={handleAddLeadsModalSelect}
                anchorEl={addLeadsButtonRef.current}
            />

            {/* Create Lead Modal */}
            <TeamV2CreateLeadModal
                open={isCreateLeadModalOpen}
                onClose={handleCreateLeadModalClose}
                onSuccess={handleCreateLeadSuccess}
                onNotification={handleNotification}
            />

        </Box>
    );
};

TeamV2LeadsMenu.propTypes = {
    totalLeads: PropTypes.number.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onAddLeads: PropTypes.func.isRequired,
    onExport: PropTypes.func,
    leads: PropTypes.array,
    selectedCount: PropTypes.number,
    onDateRangeChange: PropTypes.func,
    searchQuery: PropTypes.string,
    onSearchChange: PropTypes.func,
    onImportCSV: PropTypes.func
};

export default TeamV2LeadsMenu;